// Section for import npm components -----------------------------------------------------------------------------------
import React from 'react';
import { format, parse } from 'date-fns';

// Material UI ( mui )  components
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

// Section for import custom components --------------------------------------------------------------------------------
import DisplayUtility from '@utils/DisplayUtility';

// Section for type definition -----------------------------------------------------------------------------------------
import DisplayUtilityClass from '@/types/utils/DisplayUtilityClass';
import SalesHistoryPropsData, {
  SalesHistoryForDisplay,
  SalesHistoryForData,
  ConvertVisibleRowsData,
} from '@/types/components/MansionDetail/SalesHistoryPropsData';

type MansionSalesHistoryItemProps = {
  salesHistory: SalesHistoryPropsData;
};

type Order = 'asc' | 'desc';

type HeadCell = {
  disablePadding: boolean;
  id: keyof SalesHistoryForDisplay;
  label: { [key: string]: string };
  numeric: boolean;
};

type EnhancedTableProps = {
  order: Order;
  orderBy: string;
};

const displayUtility: DisplayUtilityClass = DisplayUtility;

function createData(
  id: number,
  salesDate: string,
  salesPrice: string,
  salesPricePerTsubo: string,
  salesPricePerSquareMeter: string,
  occupationAreaSize: number | string,
  layout: string,
  floor: string,
  facing: string,
): SalesHistoryForData {
  const parseSalesDate = parse(salesDate, 'yyyy-MM-dd', new Date());
  return {
    id,
    salesDate: parseSalesDate,
    salesPrice,
    salesPricePerTsubo,
    salesPricePerSquareMeter,
    occupationAreaSize,
    layout,
    floor,
    facing,
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string | Date }, b: { [key in Key]: number | string | Date }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells: readonly HeadCell[] = [
  {
    id: 'salesDate',
    numeric: false,
    disablePadding: true,
    label: {
      name1: '販売年月',
    },
  },
  {
    id: 'salesPrice',
    numeric: true,
    disablePadding: false,
    label: {
      name1: '販売価格',
    },
  },
  {
    id: 'salesPricePerTsubo',
    numeric: true,
    disablePadding: false,
    label: {
      name1: '坪単価',
      name2: '㎡単価',
    },
  },
  {
    id: 'occupationAreaSize',
    numeric: true,
    disablePadding: false,
    label: {
      name1: '専有面積',
      name2: '間取り',
    },
  },
  {
    id: 'floor',
    numeric: true,
    disablePadding: false,
    label: {
      name1: '所在階',
      name2: '向き',
    },
  },
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy } = props;

  return (
    <TableHead>
      <TableRow className="bg-gray-01 border-t-[1px]  border-gray-02">
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            className="px-1 border-r-[1px] border-gray-02 last:border-r-0"
          >
            <Typography className="text-sm">
              {headCell.label.name1}
              {headCell.label.name2 && ` / ${headCell.label.name2}`}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function MansionSalesHistoryList(props: MansionSalesHistoryItemProps) {
  const { salesHistory } = props;

  const [order] = React.useState<Order>('desc');
  const [orderBy] = React.useState<keyof SalesHistoryForDisplay>('salesDate');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  if (!Array.isArray(salesHistory)) return null;

  const rows = salesHistory.map((item, index) => {
    return createData(
      index + 1,
      item.salesDate ? item.salesDate : '-',
      item.salesPrice ? displayUtility.formatPriceTenThousandYen(item.salesPrice) : '-',
      item.salesPricePerTsubo ? displayUtility.formatPriceTenThousandYen(item.salesPricePerTsubo) : '-',
      item.salesPricePerSquareMeter ? displayUtility.formatPriceTenThousandYen(item.salesPricePerSquareMeter) : '-',
      item.occupationAreaSize ? item.occupationAreaSize.toString() : '-',
      item.layout || '-',
      item.floor || '-',
      item.facing || '-',
    );
  });

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRowsMemo = React.useMemo(
    () => stableSort(rows, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage],
  );

  function convertVisibleRows(visibleRow: SalesHistoryForData[]): ConvertVisibleRowsData {
    const updatedRows = visibleRow.map((val) => {
      // 日付をフォーマット
      const formattedDate = format(new Date(val.salesDate), 'yyyy年MM月');
      const formattedPrice = val.salesPrice.toString();

      // プロパティを個別に列挙して新しいオブジェクトを作成
      return {
        id: val.id,
        salesDate: formattedDate,
        salesPrice: formattedPrice,
        salesPricePerTsubo: val.salesPricePerTsubo,
        salesPricePerSquareMeter: val.salesPricePerSquareMeter,
        occupationAreaSize: val.occupationAreaSize,
        layout: val.layout,
        floor: val.floor,
        facing: val.facing,
      };
    });
    return updatedRows;
  }

  const visibleRows = convertVisibleRows(visibleRowsMemo);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);

    // 少し遅延を入れてDOMの更新を待つ（setTimeoutがないとスクロールが発生しない）
    setTimeout(() => {
      const element = document.getElementById('mansionHistoryListPc');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      }
    }, 0);
  };

  return (
    <Box>
      <Typography
        variant="h2"
        id="mansionHistoryListPc"
        className="max-md:mx-5 mb-6 max-md:mb-8 pl-4 border-solid border-l-2 border-rf-black relative text-lg font-bold before:absolute before:top-1 before:left-[-16px] max-md:before:left-0"
      >
        販売履歴
      </Typography>
      <Box className="w-full">
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 'none' }}>
          <TableContainer sx={{ overflowX: 'hidden' }}>
            <Table sx={{ minWidth: '100%' }} aria-labelledby="tableTitle" size="medium">
              <EnhancedTableHead order={order} orderBy={orderBy} />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow tabIndex={-1} key={row.id}>
                      <TableCell
                        align="center"
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        className="w-1/5 px-1 border-r-[1px]  border-gray-02"
                      >
                        <Typography className="text-sm">{row.salesDate}</Typography>
                      </TableCell>
                      <TableCell align="center" className="w-1/5 px-1 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm"> {row.salesPrice}</Typography>
                      </TableCell>
                      <TableCell align="center" className="w-1/5 px-1 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm">
                          {row.salesPricePerTsubo} / {row.salesPricePerSquareMeter}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="w-1/5 px-1 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm">
                          {row.occupationAreaSize}㎡ / {row.layout}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="w-1/5 px-1">
                        <Typography className="text-sm">
                          {row.floor} / {row.facing}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 &&
                  Array.from({ length: emptyRows }).map((_, index) => (
                    <TableRow key={`row${index + 1}`} className="h-[53px]">
                      <TableCell className="w-1/5 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm" />
                      </TableCell>
                      <TableCell align="center" className="w-1/5 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm" />
                      </TableCell>
                      <TableCell align="center" className="w-1/5 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm" />
                      </TableCell>
                      <TableCell align="center" className="w-1/5 border-r-[1px]  border-gray-02">
                        <Typography className="text-sm" />
                      </TableCell>
                      <TableCell align="center">
                        <Typography className="w-1/5 text-sm" />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="表示件数"
            SelectProps={{
              sx: {
                svg: { color: '#119CDA' },
                overflowX: 'hidden',
                marginLeft: '0',
                marginRight: '20px',
              },
            }}
            labelDisplayedRows={({ count }) => `全${count}件`}
            sx={{
              '.MuiIconButton-root': {
                color: rows.length <= 5 ? '#B7B7B7' : '#119CDA', // 条件に応じてアイコンの色を変更
              },
            }}
            className="bg-gray-01 border-b-[1px]  border-gray-02"
          />
        </Paper>
      </Box>
    </Box>
  );
}
